// familyTreeModel.js — the shared model behind the family tree.
//
// Named *Model* deliberately: src/FamilyTree.js already exists, and macOS is
// case-insensitive, so a file called familyTree.js silently overwrites it.
//
// The tree used to store a fixed TIER string ('grandparents', 'parents',
// 'self', 'children', 'grandchildren'), which capped ancestry at two
// generations back. Families do not stop there — people want their
// great-grandparents, and the generation before that, and the one before that.
//
// So position is now a signed GENERATION integer:
//
//    -3  great-grandparents        0  the memorial's own generation
//    -2  grandparents             +1  children
//    -1  parents                  +2  grandchildren
//
// Any depth is representable, in either direction, and the row heading is
// generated rather than looked up. The old `tier` field is still read, so
// every tree built before this change keeps working untouched.

export const TIER_TO_GENERATION = {
  grandparents: -2,
  parents: -1,
  self: 0,
  children: 1,
  grandchildren: 2,
};

/** Where this person sits, tolerating documents written before generations. */
export function generationOf(person) {
  if (!person) return 0;
  if (Number.isFinite(person.generation)) return person.generation;
  const fromTier = TIER_TO_GENERATION[person.tier];
  return Number.isFinite(fromTier) ? fromTier : 0;
}

/** Kept so older readers of the data still find a tier they understand. */
export function tierForGeneration(generation) {
  if (generation <= -2) return 'grandparents';
  if (generation === -1) return 'parents';
  if (generation === 1) return 'children';
  if (generation >= 2) return 'grandchildren';
  return 'self';
}

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * The heading for a generation row, at any depth.
 *   -1 Parents · -2 Grandparents · -3 Great-Grandparents
 *   -4 2× Great-Grandparents · -5 3× Great-Grandparents …
 */
export function generationLabel(generation) {
  if (generation === 0) return 'Their Generation';
  const depth = Math.abs(generation);
  const up = generation < 0;
  const base = up ? 'Grandparents' : 'Grandchildren';
  if (depth === 1) return up ? 'Parents' : 'Children';
  if (depth === 2) return base;
  if (depth === 3) return `Great-${base}`;
  return `${depth - 2}× Great-${base}`;
}

/** A quieter subtitle, so a deep row still reads as a human relationship. */
export function generationHint(generation) {
  if (generation === 0) return '';
  const depth = Math.abs(generation);
  if (depth <= 3) return '';
  return generation < 0
    ? `${ordinal(depth)} generation back`
    : `${ordinal(depth)} generation on`;
}

// Relations offered in the picker. `generation` positions the card; anything
// deeper than great-grandparents is reached through the two DEPTH_* entries,
// which reveal a "generations back" control rather than bloating this list
// with 3×, 4×, 5× great-grandparents forever.
export const DEEP_ANCESTOR = 'Earlier ancestor';
export const DEEP_DESCENDANT = 'Later descendant';

export const RELATIONS = [
  { value: 'Great-Grandfather', generation: -3 },
  { value: 'Great-Grandmother', generation: -3 },
  { value: 'Grandfather',       generation: -2 },
  { value: 'Grandmother',       generation: -2 },
  { value: 'Great-Aunt',        generation: -2 },
  { value: 'Great-Uncle',       generation: -2 },
  { value: 'Father',            generation: -1 },
  { value: 'Mother',            generation: -1 },
  { value: 'Step-parent',       generation: -1 },
  { value: 'Aunt',              generation: -1 },
  { value: 'Uncle',             generation: -1 },
  { value: 'Spouse',            generation: 0 },
  { value: 'Partner',           generation: 0 },
  { value: 'Brother',           generation: 0 },
  { value: 'Sister',            generation: 0 },
  { value: 'Cousin',            generation: 0 },
  { value: 'Son',               generation: 1 },
  { value: 'Daughter',          generation: 1 },
  { value: 'Niece',             generation: 1 },
  { value: 'Nephew',            generation: 1 },
  { value: 'Grandson',          generation: 2 },
  { value: 'Granddaughter',     generation: 2 },
  { value: 'Great-Grandchild',  generation: 3 },
  { value: DEEP_ANCESTOR,       generation: -4, deep: true },
  { value: DEEP_DESCENDANT,     generation: 4,  deep: true },
];

export function relationMeta(relation) {
  return RELATIONS.find((r) => r.value === relation) || null;
}

export function isDeepRelation(relation) {
  const meta = relationMeta(relation);
  return !!(meta && meta.deep);
}

/**
 * The generation a chosen relation lands in. `depth` only applies to the two
 * open-ended relations, and is how far back (or on) the person sits.
 */
export function generationForRelation(relation, depth) {
  const meta = relationMeta(relation);
  if (!meta) return 0;
  if (!meta.deep) return meta.generation;
  const d = Math.max(4, Math.min(12, Number(depth) || 4));
  return meta.generation < 0 ? -d : d;
}

/** Rows to render: every generation that has someone in it, oldest first. */
export function buildGenerationRows(people, selfCard) {
  const byGeneration = new Map();
  for (const person of people || []) {
    const g = generationOf(person);
    if (!byGeneration.has(g)) byGeneration.set(g, []);
    byGeneration.get(g).push(person);
  }
  if (selfCard) {
    if (!byGeneration.has(0)) byGeneration.set(0, []);
    byGeneration.get(0).unshift(selfCard);
  }
  return [...byGeneration.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([generation, members]) => ({
      generation,
      key: `gen_${generation}`,
      label: generationLabel(generation),
      hint: generationHint(generation),
      members,
    }));
}
